import styled from '@emotion/styled';
import * as React from 'react';
import { FC, useEffect, useMemo } from 'react';
import useSWR from 'swr';

import FooterActions from '@/components/FooterActions';
import Html2React from '@/features/html2react';
import { EDITOR_CONTENT_CLASS } from '@/features/html2react/constants';
import PageHead from '@/features/page/components/PageHead';
import PageContext from '@/features/page/context';
import { GeneralSettings } from '@/types/setting';

import { processors } from './processors/functions/processors';
import { Page } from './types';

const PageSingle: FC<Page & { isHomepage?: boolean }> = ({ isHomepage = false, ...page }) => {
  const { title, rawBody, template } = page;

  const { data: settings } = useSWR<GeneralSettings>(`/setting/public/general/client`);

  const StyledTemplate = useMemo(
    () => styled.div`
      .${EDITOR_CONTENT_CLASS} {
        ${template?.style ?? ''}
      }
    `,
    [template?.style]
  );

  const memoizedProcessors = useMemo(() => processors(), []);

  useEffect(() => {
    if (!template?.scripts) return;

    const scriptId = `template-script-${template.id}`;

    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'text/javascript';
    script.innerHTML = template.scripts;
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [template?.id, template?.scripts]);

  const pageContextValue = useMemo(() => ({ ...page, generalSettings: settings }), [page, settings]);

  return (
    <PageContext.Provider value={pageContextValue}>
      <PageHead title={title} meta={page.meta} isHomepage={isHomepage} />
      <StyledTemplate>
        <Html2React processors={memoizedProcessors}>{rawBody}</Html2React>
      </StyledTemplate>
      <FooterActions />
    </PageContext.Provider>
  );
};

export default PageSingle;
